"use strict";
//
// Copyright © 2020, 2021 Anticrm Platform Contributors.
// Copyright © 2026 Intabia Fusion.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.LiveQuery = void 0;
const analytics_1 = require("@hcengineering/analytics");
const core_1 = __importStar(require("@hcengineering/core"));
const platform_1 = require("@hcengineering/platform");
const fast_equals_1 = require("fast-equals");
const refs_1 = require("./refs");
const results_1 = require("./results");
const CACHE_SIZE = 125;
/**
 * @public
 */
class LiveQuery {
    client;
    queries = new Map();
    queue = new Map();
    queryCounter = 0;
    closed = false;
    queriesToUpdate = new Map();
    refs = new refs_1.Refs(() => this.getHierarchy());
    constructor(client) {
        this.client = client;
    }
    isClosed() {
        return this.closed;
    }
    async close() {
        this.closed = true;
        await this.client.close();
    }
    getHierarchy() {
        return this.client.getHierarchy();
    }
    getModel() {
        return this.client.getModel();
    }
    // Drop idle queries when reconnect gap exceeds this window.
    // Idle = sits in queue with no active callbacks. Holding stale results across
    // long disconnects (sleep/network drop) is wasteful — drop and let next
    // subscription refetch fresh.
    static IDLE_DROP_GAP_MS = 5000;
    // Concurrency cap for parallel refresh after reconnect — avoid request storm.
    static REFRESH_CONCURRENCY = 4;
    // Perform refresh of content since connection established.
    // `lastReconnectGapMs` — ms since previous successful connection. When the
    // gap is large, drop idle queries instead of refreshing them.
    // Returns stats so the caller can log/track it.
    async refreshConnect(clean, lastReconnectGapMs = 0) {
        const dropIdle = lastReconnectGapMs > LiveQuery.IDLE_DROP_GAP_MS;
        const sizeOf = (q) => (q.result instanceof Promise ? 0 : q.result.length);
        const stats = {
            gapMs: lastReconnectGapMs,
            dropIdle,
            droppedQueries: 0,
            droppedDocs: 0,
            activeQueries: 0,
            activeDocs: 0,
            droppedByClass: new Map(),
            activeByClass: new Map()
        };
        for (const q of [...this.queue.values()]) {
            if (q.callbacks.size === 0) {
                // No active callbacks. On long gap drop the cached result; otherwise
                // leave it — TTL eviction will clean it.
                if (dropIdle) {
                    const docs = sizeOf(q);
                    stats.droppedQueries++;
                    stats.droppedDocs += docs;
                    const key = q._class;
                    const prev = stats.droppedByClass.get(key) ?? { count: 0, docs: 0 };
                    prev.count++;
                    prev.docs += docs;
                    stats.droppedByClass.set(key, prev);
                    this.removeQueue(q);
                }
                continue;
            }
            // Has callbacks but somehow ended up in queue — keep going to refresh below.
        }
        // Collect active subscriptions, refresh smallest first so quick queries
        // unblock UI before heavy ones.
        const active = [];
        for (const v of this.queries.values()) {
            for (const q of v.values()) {
                if (q.callbacks.size === 0)
                    continue;
                active.push(q);
                const docs = sizeOf(q);
                stats.activeQueries++;
                stats.activeDocs += docs;
                const key = q._class;
                const prev = stats.activeByClass.get(key) ?? { count: 0, docs: 0 };
                prev.count++;
                prev.docs += docs;
                stats.activeByClass.set(key, prev);
            }
        }
        active.sort((a, b) => sizeOf(a) - sizeOf(b));
        const limiter = new core_1.RateLimiter(LiveQuery.REFRESH_CONCURRENCY);
        for (const q of active) {
            if (clean) {
                this.cleanQuery(q);
            }
            void limiter.add(async () => {
                try {
                    await this.refresh(q);
                }
                catch (err) {
                    if (err instanceof platform_1.PlatformError && err.message === 'connection closed')
                        return;
                    analytics_1.Analytics.handleError(err);
                    console.error(err);
                }
            });
        }
        await limiter.waitProcessing();
        return stats;
    }
    cleanQuery(q) {
        q.callbacks.forEach((callback) => {
            callback((0, core_1.toFindResult)([], 0));
        });
        q.result = new results_1.ResultArray([], this.getHierarchy());
        q.total = -1;
    }
    match(q, doc, skipLookup = false) {
        if (this.getHierarchy().isMixin(q._class)) {
            if (this.getHierarchy().hasMixin(doc, q._class)) {
                doc = this.getHierarchy().as(doc, q._class);
            }
            else {
                return false;
            }
        }
        if (!this.getHierarchy().isDerived(doc._class, q._class)) {
            // Check if it is not a mixin and not match class
            const mixinClass = core_1.Hierarchy.mixinClass(doc);
            if (mixinClass === undefined || !this.getHierarchy().isDerived(mixinClass, q._class)) {
                return false;
            }
        }
        const query = q.query;
        for (const key in query) {
            if (key === '$search')
                continue;
            if (key === '$searchStrict')
                continue;
            if (skipLookup && key.startsWith('$lookup'))
                continue;
            const value = query[key];
            const result = (0, core_1.findProperty)([doc], key, value);
            if (result.length === 0) {
                return false;
            }
        }
        return true;
    }
    createDumpQuery(_class, query, options) {
        const q = this.createQuery(_class, query, undefined, options);
        this.queue.set(q.id, { ...q, lastUsed: (0, core_1.platformNow)() });
        if (!(q.result instanceof Promise)) {
            q.result.clean();
        }
        return q;
    }
    async findAll(_class, query, options) {
        if (this.client.getHierarchy().getDomain(_class) === core_1.DOMAIN_MODEL) {
            return await this.client.findAll(_class, query, options);
        }
        const opt = { ...(options ?? {}) };
        if (opt.projection !== undefined) {
            opt.projection = {
                ...opt.projection,
                _class: 1,
                space: 1,
                modifiedOn: 1
            };
        }
        // Perform one document queries if applicable.
        const d = this.refs.findFromDocs(_class, query, opt);
        if (d !== null) {
            return d;
        }
        const q = this.findQuery(_class, query, opt) ?? this.createDumpQuery(_class, query, opt);
        if (q.result instanceof Promise) {
            q.result = await q.result;
        }
        if (this.removeFromQueue(q, false)) {
            this.queue.set(q.id, { ...q, lastUsed: (0, core_1.platformNow)() });
            q.result.clean();
        }
        return (0, core_1.toFindResult)(q.result.getClone(), q.total);
    }
    async domainRequest(domain, params, options) {
        return await this.client.domainRequest(domain, params, options);
    }
    searchFulltext(query, options) {
        return this.client.searchFulltext(query, options);
    }
    async findOne(_class, query, options) {
        if (this.client.getHierarchy().getDomain(_class) === core_1.DOMAIN_MODEL) {
            return await this.client.findOne(_class, query, options);
        }
        if (options?.projection !== undefined) {
            options.projection = {
                ...options.projection,
                _class: 1,
                space: 1,
                modifiedOn: 1
            };
        }
        options ??= {};
        options.limit = 1;
        const d = this.refs.findFromDocs(_class, query, options);
        if (d !== null) {
            return d[0];
        }
        const q = this.findQuery(_class, query, options) ?? this.createDumpQuery(_class, query, options);
        if (q.result instanceof Promise) {
            q.result = await q.result;
        }
        if (this.removeFromQueue(q, false)) {
            this.queue.set(q.id, { ...q, lastUsed: (0, core_1.platformNow)() });
            q.result.clean();
        }
        return q.result.getClone().shift();
    }
    optionsCompare(opt1, opt2) {
        const { ctx: _1, ..._opt1 } = (opt1 ?? {});
        const { ctx: _2, ..._opt2 } = (opt2 ?? {});
        return (0, fast_equals_1.deepEqual)(_opt1, _opt2);
    }
    queryCompare(q1, q2) {
        if (Object.keys(q1).length !== Object.keys(q2).length) {
            return false;
        }
        return (0, fast_equals_1.deepEqual)(q1, q2);
    }
    findQuery(_class, query, options) {
        const queries = this.getQueueMap(_class);
        if (queries === undefined)
            return;
        for (const q of queries.values()) {
            if (!this.queryCompare(query, q.query) || !this.optionsCompare(options, q.options))
                continue;
            return q;
        }
    }
    removeFromQueue(q, update = true) {
        if (q.callbacks.size === 0) {
            const removed = this.queue.delete(q.id);
            if (removed) {
                if (update) {
                    if (!(q.result instanceof Promise)) {
                        this.refs.updateDocuments(q, q.result.getDocs(), true);
                    }
                }
                return true;
            }
        }
        return false;
    }
    pushCallback(q, callback) {
        q.callbacks.set(callback.callbackId, callback.callback);
        setTimeout(async () => {
            if (q !== undefined) {
                if (q.result instanceof Promise) {
                    q.result = await q.result;
                }
                callback.callback((0, core_1.toFindResult)(q.result.getResult(callback.callbackId), q.total));
            }
        }, 0);
    }
    getQuery(_class, query, callback, options) {
        const current = this.findQuery(_class, query, options);
        if (current !== undefined) {
            this.removeFromQueue(current, false);
            this.pushCallback(current, callback);
            return current;
        }
    }
    getQueueMap(_class) {
        let cq = this.queries.get(_class);
        if (cq === undefined) {
            cq = new Map();
            this.queries.set(_class, cq);
        }
        return cq;
    }
    createQuery(_class, query, callback, options) {
        const _query = (0, core_1.clone)(query);
        const localResult = this.refs.findFromDocs(_class, query, options);
        const result = localResult != null ? Promise.resolve(localResult) : this.client.findAll(_class, query, options);
        const q = {
            id: ++this.queryCounter,
            _class,
            query: _query,
            result: result.then((docs) => new results_1.ResultArray(docs, this.getHierarchy())),
            total: 0,
            options,
            callbacks: new Map(),
            refresh: (0, core_1.reduceCalls)(() => this.doRefresh(q)),
            refreshId: 0
        };
        if (callback !== undefined) {
            q.callbacks.set(callback.callbackId, callback.callback);
        }
        this.getQueueMap(_class).set(q.id, q);
        result
            .then(async (result) => {
            q.total = result.total;
            await this.callback(q);
        })
            .catch((err) => {
            analytics_1.Analytics.handleError(err);
            console.log('failed to update Live Query: ', err);
        });
        if (this.queue.size > CACHE_SIZE) {
            this.remove();
        }
        return q;
    }
    remove() {
        const used = Array.from(this.queue.values()).sort((a, b) => a.lastUsed - b.lastUsed);
        // Remove enough queries to bring the queue back down to 80% of CACHE_SIZE
        // This prevents constant cleanup cycles
        const targetSize = Math.floor(CACHE_SIZE * 0.8);
        const toRemove = Math.max(0, this.queue.size - targetSize);
        for (let i = 0; i < toRemove; i++) {
            const q = used.shift();
            if (q === undefined)
                return;
            this.removeQueue(q);
        }
    }
    removeQueue(q) {
        const queries = this.getQueueMap(q._class);
        const removed = queries.delete(q.id);
        this.queue.delete(q.id);
        if (removed) {
            if (!(q.result instanceof Promise)) {
                this.refs.updateDocuments(q, q.result.getDocs(), true);
            }
        }
    }
    query(_class, query, callback, options) {
        if (options?.projection !== undefined) {
            options.projection = {
                ...options.projection,
                _class: 1,
                space: 1,
                modifiedOn: 1
            };
        }
        const callbackId = (0, core_1.generateId)();
        const q = this.getQuery(_class, query, { callback: callback, callbackId }, options) ??
            this.createQuery(_class, query, { callback, callbackId }, options);
        return () => {
            q.callbacks.delete(callbackId);
            if (q.callbacks.size === 0) {
                if (!(q.result instanceof Promise)) {
                    q.result.clean();
                }
                this.queue.set(q.id, { ...q, lastUsed: (0, core_1.platformNow)() });
                // Check if we need to clean up the queue after adding this query
                if (this.queue.size > CACHE_SIZE) {
                    this.remove();
                }
            }
        };
    }
    async queryFind(_class, query, options) {
        if (options?.projection !== undefined) {
            options.projection = {
                ...options.projection,
                _class: 1,
                space: 1,
                modifiedOn: 1
            };
        }
        const current = this.findQuery(_class, query, options);
        if (current === undefined) {
            const q = this.createQuery(_class, query, undefined, // No need of callback
            options);
            if (q.result instanceof Promise) {
                q.result = await q.result;
            }
            return (0, core_1.toFindResult)(q.result.getClone(), q.total);
        }
        if (current.result instanceof Promise) {
            current.result = await current.result;
        }
        return (0, core_1.toFindResult)(current.result.getClone(), current.total);
    }
    async checkSearch(q, _id) {
        const match = await this.client.findOne(q._class, { $search: q.query.$search, $searchStrict: q.query.$searchStrict, _id }, q.options);
        if (q.result instanceof Promise) {
            q.result = await q.result;
        }
        if (match === undefined) {
            if (q.options?.limit === q.result.length) {
                await this.refresh(q);
                return true;
            }
            else {
                const doc = q.result.delete(_id);
                if (doc !== undefined) {
                    this.refs.updateDocuments(q, [doc], true);
                    if (q.options?.total === true) {
                        q.total--;
                    }
                }
            }
        }
        else {
            const doc = q.result.findDoc(_id);
            if (doc !== undefined) {
                q.result.updateDoc(match, false);
                this.refs.updateDocuments(q, [match]);
            }
        }
        return false;
    }
    async getDocFromCache(docCache, _id, _class, space, q) {
        const options = {};
        if (q.options?.associations !== undefined) {
            options.associations = q.options?.associations;
        }
        if (q.options?.lookup !== undefined) {
            options.lookup = q.options?.lookup;
        }
        const showArchived = (0, core_1.shouldShowArchived)(q.query, q.options);
        options.showArchived = showArchived;
        const docIdKey = _id + JSON.stringify(options ?? {}) + q._class;
        const current = docCache.get(docIdKey) ?? (await this.client.findOne(q._class, { _id, space }, options));
        if (current !== undefined) {
            docCache.set(docIdKey, current);
        }
        else {
            docCache.delete(docIdKey);
        }
        return current;
    }
    asMixin(doc, mixin) {
        if (this.getHierarchy().isMixin(mixin)) {
            return this.getHierarchy().as(doc, mixin);
        }
        return doc;
    }
    async getCurrentDoc(q, _id, space, docCache) {
        let current = await this.getDocFromCache(docCache, _id, q._class, space, q);
        if (q.result instanceof Promise) {
            q.result = await q.result;
        }
        const pos = q.result.findDoc(_id);
        if (current !== undefined) {
            current = this.asMixin(current, q._class);
        }
        if (current !== undefined && this.match(q, current)) {
            // Clone: docCache hands the same object to every query in the batch, and later $inc txes
            // are applied per query - a shared object would be incremented once per subscriber.
            q.result.updateDoc(current);
            this.refs.updateDocuments(q, [current]);
        }
        else {
            if (q.options?.limit === q.result.length) {
                await this.refresh(q);
                return true;
            }
            else if (pos !== undefined) {
                q.result.delete(_id);
                this.refs.updateDocuments(q, [pos], true);
                if (q.options?.total === true) {
                    q.total--;
                }
            }
        }
        return false;
    }
    async __updateMixinDoc(q, updatedDoc, tx) {
        updatedDoc = core_1.TxProcessor.updateMixin4Doc(updatedDoc, tx);
        const ops = {
            ...tx.attributes,
            modifiedBy: tx.modifiedBy,
            modifiedOn: tx.modifiedOn
        };
        await this.__updateLookup(q, updatedDoc, ops);
    }
    checkUpdatedDocMatch(q, result, updatedDoc) {
        if (!this.match(q, updatedDoc)) {
            if (q.options?.limit === result.length) {
                void this.refresh(q);
                return true;
            }
            else {
                result.delete(updatedDoc._id);
                this.refs.updateDocuments(q, [updatedDoc], true);
                if (q.options?.total === true) {
                    q.total--;
                }
            }
        }
        else {
            result.updateDoc(updatedDoc, false);
            this.refs.updateDocuments(q, [updatedDoc]);
        }
        return false;
    }
    async txMixin(tx, docCache) {
        const hierarchy = this.client.getHierarchy();
        for (const queries of this.queries.entries()) {
            const isTx = hierarchy.isDerived(queries[0], core_1.default.class.Tx);
            for (const q of queries[1].values()) {
                if (isTx) {
                    // handle add since Txes are immutable
                    if (this.match(q, tx, q.options?.lookup !== undefined)) {
                        await this.handleDocAdd(q, tx, true, docCache);
                    }
                    await this.handleDocAddLookup(q, tx);
                    continue;
                }
                if (q.result instanceof Promise) {
                    q.result = await q.result;
                }
                let updatedDoc = q.result.findDoc(tx.objectId);
                if (updatedDoc !== undefined) {
                    // If query contains search we must check use fulltext
                    if (q.query.$search != null && q.query.$search.length > 0) {
                        const searchRefresh = await this.checkSearch(q, tx.objectId);
                        if (searchRefresh) {
                            continue;
                        }
                    }
                    else {
                        if (updatedDoc.modifiedOn < tx.modifiedOn) {
                            await this.__updateMixinDoc(q, updatedDoc, tx);
                            updatedDoc = this.asMixin(updatedDoc, q._class);
                            const updateRefresh = this.checkUpdatedDocMatch(q, q.result, updatedDoc);
                            if (updateRefresh) {
                                continue;
                            }
                        }
                        else {
                            const currentRefresh = await this.getCurrentDoc(q, updatedDoc._id, updatedDoc.space, docCache);
                            if (currentRefresh) {
                                continue;
                            }
                        }
                    }
                    await this.sort(q, tx);
                    const udoc = q.result.findDoc(tx.objectId);
                    await this.updatedDocCallback(q, q.result, udoc);
                }
                else if (queries[0] === tx.mixin) {
                    // Mixin potentially added to object we doesn't have in out results
                    const doc = await this.client.findOne(q._class, { ...q.query, _id: tx.objectId }, q.options);
                    if (doc !== undefined) {
                        if (this.match(q, doc, q.options?.lookup !== undefined)) {
                            await this.handleDocAdd(q, doc, false, docCache);
                        }
                        await this.handleDocAddLookup(q, doc);
                    }
                }
                await this.handleDocUpdateLookup(q, tx);
                await this.handleDocUpdateRelation(q, tx);
            }
        }
        return {};
    }
    async txUpdateDoc(tx, docCache) {
        for (const queries of this.queries.entries()) {
            const isTx = this.client.getHierarchy().isDerived(queries[0], core_1.default.class.Tx);
            for (const q of queries[1].values()) {
                if (isTx) {
                    // handle add since Txes are immutable
                    // await this.handleDocAdd(q, tx, true, docCache)
                    if (this.match(q, tx, q.options?.lookup !== undefined)) {
                        await this.handleDocAdd(q, tx, true, docCache);
                    }
                    await this.handleDocAddLookup(q, tx);
                    continue;
                }
                await this.handleDocUpdate(q, tx, docCache);
            }
        }
        return {};
    }
    async handleDocUpdate(q, tx, docCache) {
        if (q.result instanceof Promise) {
            q.result = await q.result;
        }
        const updatedDoc = q.result.findDoc(tx.objectId);
        if (updatedDoc !== undefined) {
            // If query contains search we must check use fulltext
            if (q.query.$search != null && q.query.$search.length > 0) {
                const searchRefresh = await this.checkSearch(q, tx.objectId);
                if (searchRefresh)
                    return;
            }
            else {
                // $inc is commutative, so an equal-timestamp $inc-only tx (derived counter
                // txes share the parent tx timestamp) is applied locally instead of
                // re-fetching the doc from the server — UNLESS the doc was freshly loaded
                // from the server at this exact modifiedOn timestamp (in which case it already
                // incorporates the $inc from the database).
                const isLoadedAtSameTs = q.result.isLoadedAtModifiedOn(tx.objectId, tx.modifiedOn);
                const incOnlyAtSameTime = !isLoadedAtSameTs &&
                    updatedDoc.modifiedOn === tx.modifiedOn &&
                    tx.operations.$inc != null &&
                    Object.keys(tx.operations).every((k) => k === '$inc');
                if (updatedDoc.modifiedOn < tx.modifiedOn || incOnlyAtSameTime || tx.meta?.forceApply === true) {
                    await this.__updateDoc(q, updatedDoc, tx);
                    const updateRefresh = this.checkUpdatedDocMatch(q, q.result, updatedDoc);
                    if (updateRefresh) {
                        return;
                    }
                }
                else {
                    const currentRefresh = await this.getCurrentDoc(q, updatedDoc._id, updatedDoc.space, docCache);
                    if (currentRefresh) {
                        return;
                    }
                }
            }
            await this.sort(q, tx);
            const udoc = q.result.findDoc(tx.objectId);
            await this.updatedDocCallback(q, q.result, udoc);
        }
        else if (this.matchQuerySync(q, tx) && (await this.matchQuery(q, tx, docCache))) {
            await this.sort(q, tx);
            const udoc = q.result.findDoc(tx.objectId);
            await this.updatedDocCallback(q, q.result, udoc);
        }
        else if (this.client.getHierarchy().isDerived(tx.objectClass, q._class) &&
            q.options?.total === true &&
            q.options.limit === q.result.length) {
            // we can make object is not matching criteria, but it can be in not limited results, total can be changed
            await this.refresh(q);
            return;
        }
        await this.handleDocUpdateLookup(q, tx);
        await this.handleDocUpdateRelation(q, tx);
    }
    isPossibleAssociationTx(tx, association) {
        const h = this.getHierarchy();
        const byClass = h.isDerived(tx.objectClass, association.classA) ||
            h.isDerived(tx.objectClass, association.classB) ||
            h.isDerived(association.classA, tx.objectClass) ||
            h.isDerived(association.classB, tx.objectClass);
        if (byClass) {
            return true;
        }
        if (tx._class === core_1.default.class.TxMixin) {
            const mixinTx = tx;
            return h.isDerived(mixinTx.mixin, association.classA) || h.isDerived(mixinTx.mixin, association.classB);
        }
        return false;
    }
    async handleDocUpdateRelation(q, tx) {
        if (q.options?.associations === undefined)
            return;
        for (const assoc of q.options.associations) {
            const association = this.getModel().findObject(assoc[0]);
            if (association === undefined)
                continue;
            if (this.isPossibleAssociationTx(tx, association)) {
                if (q.result instanceof Promise) {
                    q.result = await q.result;
                }
                const docs = q.result.getDocs();
                for (const doc of docs) {
                    const docToUpdate = doc.$associations?.[`${association._id}_a`]?.find((it) => it._id === tx.objectId) ??
                        doc.$associations?.[`${association._id}_b`]?.find((it) => it._id === tx.objectId);
                    if (docToUpdate !== undefined) {
                        if (tx._class === core_1.default.class.TxMixin) {
                            core_1.TxProcessor.updateMixin4Doc(docToUpdate, tx);
                        }
                        else {
                            core_1.TxProcessor.updateDoc2Doc(docToUpdate, tx);
                        }
                        q.result.updateDoc(doc, false);
                        this.queriesToUpdate.set(q.id, q);
                    }
                }
            }
        }
    }
    async handleDocUpdateLookup(q, tx) {
        if (q.options?.lookup === undefined)
            return;
        const lookup = q.options.lookup;
        if (q.result instanceof Promise) {
            q.result = await q.result;
        }
        let needCallback = false;
        needCallback = await this.processLookupUpdateDoc(q.result, lookup, tx);
        if (needCallback) {
            if (q.options?.sort !== undefined) {
                q.result.sort(q._class, q.options.sort, this.getHierarchy(), this.client.getModel());
            }
            await this.callback(q, true);
        }
    }
    async processLookupUpdateDoc(docs, lookup, tx) {
        let needCallback = false;
        const lookupWays = this.getLookupWays(lookup, tx.objectClass);
        for (const lookupWay of lookupWays) {
            const [objWay, key, reverseLookupKey] = lookupWay;
            for (const resDoc of docs.getDocs()) {
                const obj = (0, core_1.getObjectValue)(objWay, resDoc);
                if (obj === undefined)
                    continue;
                const value = (0, core_1.getObjectValue)('$lookup.' + key, obj);
                if (Array.isArray(value)) {
                    let index = value.findIndex((p) => p._id === tx.objectId);
                    if (this.client.getHierarchy().isDerived(tx.objectClass, core_1.default.class.AttachedDoc)) {
                        if (reverseLookupKey !== undefined) {
                            const reverseLookupValue = (tx._class === core_1.default.class.TxMixin
                                ? tx.attributes
                                : tx.operations)[reverseLookupKey];
                            if (index !== -1 && reverseLookupValue !== undefined && reverseLookupValue !== obj._id) {
                                value.splice(index, 1);
                                index = -1;
                                needCallback = true;
                                docs.updateDoc(resDoc, false);
                            }
                            else if (index === -1 && reverseLookupValue === obj._id) {
                                const doc = await this.findOne(tx.objectClass, { _id: tx.objectId });
                                if (doc !== undefined) {
                                    value.push(doc);
                                    index = value.length - 1;
                                }
                                needCallback = true;
                                docs.updateDoc(resDoc, false);
                            }
                        }
                    }
                    if (index !== -1) {
                        if (tx._class === core_1.default.class.TxMixin) {
                            core_1.TxProcessor.updateMixin4Doc(value[index], tx);
                        }
                        else {
                            core_1.TxProcessor.updateDoc2Doc(value[index], tx);
                        }
                        needCallback = true;
                        docs.updateDoc(resDoc, false);
                    }
                }
                else {
                    if (obj[key] === tx.objectId) {
                        if (obj.$lookup[key] !== undefined) {
                            if (tx._class === core_1.default.class.TxMixin) {
                                core_1.TxProcessor.updateMixin4Doc(obj.$lookup[key], tx);
                            }
                            else {
                                core_1.TxProcessor.updateDoc2Doc(obj.$lookup[key], tx);
                            }
                            needCallback = true;
                            docs.updateDoc(resDoc, false);
                        }
                    }
                }
            }
        }
        return needCallback;
    }
    async refresh(q) {
        this.queriesToUpdate.delete(q.id);
        await q.refresh();
    }
    async doRefresh(q) {
        const qid = ++q.refreshId;
        const res = await this.client.findAll(q._class, q.query, q.options);
        if (q.refreshId === qid && (!(0, fast_equals_1.deepEqual)(res, q.result) || (res.total !== q.total && q.options?.total === true))) {
            q.result = new results_1.ResultArray(res, this.getHierarchy());
            q.total = res.total;
            await this.callback(q);
        }
    }
    matchQuerySync(q, tx) {
        const clazz = this.getHierarchy().isMixin(q._class) ? this.getHierarchy().getBaseClass(q._class) : q._class;
        const target = tx.operations._class ?? tx.objectClass;
        if (!this.client.getHierarchy().isDerived(target, clazz)) {
            return false;
        }
        return true;
    }
    // Check if query is partially matched.
    async matchQuery(q, tx, docCache) {
        const doc = {
            _id: tx.objectId,
            _class: tx.objectClass,
            modifiedBy: tx.modifiedBy,
            modifiedOn: tx.modifiedOn,
            space: tx.objectSpace
        };
        // we cannot handle $inc correctly, let's skip it
        const { $inc, ...ops } = tx.operations;
        const emptyOps = Object.keys(ops).length === 0;
        if (emptyOps && $inc != null) {
            // $inc-only tx (e.g. collection counters): the doc was not in the result, and a
            // counter change cannot start matching unless the query/sort references that field.
            const incTouchesQuery = Object.keys($inc).some((k) => q.query[k] !== undefined || q.options?.sort?.[k] !== undefined);
            if (!incTouchesQuery) {
                return false;
            }
        }
        let matched = emptyOps || Object.keys(q.query).length === 0;
        if (!emptyOps) {
            const virtualTx = {
                ...tx,
                operations: ops
            };
            core_1.TxProcessor.updateDoc2Doc(doc, virtualTx);
            for (const key in q.query) {
                const value = q.query[key];
                const tkey = (0, core_1.checkMixinKey)(key, q._class, this.client.getHierarchy());
                if (doc[tkey] === undefined)
                    continue;
                const res = (0, core_1.findProperty)([doc], tkey, value);
                if (res.length === 0) {
                    return false;
                }
                else {
                    matched = true;
                }
            }
        }
        if (matched) {
            const realDoc = await this.getDocFromCache(docCache, doc._id, core_1.Hierarchy.mixinOrClass(doc), doc.space, q);
            if (realDoc == null)
                return false;
            if (this.getHierarchy().isMixin(q._class)) {
                if (!this.getHierarchy().hasMixin(realDoc, q._class)) {
                    return false;
                }
            }
            const res = (0, core_1.matchQuery)([realDoc], q.query, q._class, this.client.getHierarchy());
            if (res.length === 1) {
                if (q.result instanceof Promise) {
                    q.result = await q.result;
                }
                const doc = res[0];
                const pos = q.result.findDoc(doc._id);
                if (pos !== undefined) {
                    q.result.updateDoc(doc);
                    this.refs.updateDocuments(q, [doc]);
                }
                else {
                    q.result.push(doc);
                    if (q.options?.total === true) {
                        q.total++;
                    }
                }
                return true;
            }
        }
        return false;
    }
    async getLookupValue(_class, doc, lookup, result) {
        for (const key in lookup) {
            if (key === '_id') {
                await this.getReverseLookupValue(doc, lookup, result);
                continue;
            }
            const value = lookup[key];
            const tkey = (0, core_1.checkMixinKey)(key, _class, this.client.getHierarchy());
            const refId = (0, core_1.getObjectValue)(tkey, doc);
            if (refId == null)
                continue;
            if (Array.isArray(value)) {
                const [_class, nested] = value;
                result[key] = await this.findOne(_class, { _id: refId });
                const nestedResult = {};
                const parent = result[key];
                if (parent !== undefined) {
                    await this.getLookupValue(_class, parent, nested, nestedResult);
                    Object.assign(parent, {
                        $lookup: nestedResult
                    });
                }
            }
            else {
                ;
                result[key] = await this.findOne(value, { _id: refId });
            }
        }
    }
    async getReverseLookupValue(doc, lookup, result) {
        for (const key in lookup._id) {
            if (doc[key] === undefined || doc[key] === 0) {
                continue;
            }
            const value = lookup._id[key];
            let _class;
            let attr = 'attachedTo';
            if (Array.isArray(value)) {
                _class = value[0];
                attr = value[1];
            }
            else {
                _class = value;
            }
            ;
            result[key] = await this.findAll(_class, { [attr]: doc._id });
        }
    }
    async lookup(_class, doc, lookup) {
        const result = {};
        await this.getLookupValue(_class, doc, lookup, result);
        doc.$lookup = result;
    }
    async txCreateDoc(tx, docCache) {
        const docTx = core_1.TxProcessor.createDoc2Doc(tx);
        for (const queries of this.queries.entries()) {
            const doc = this.client.getHierarchy().isDerived(queries[0], core_1.default.class.Tx) ? tx : docTx;
            for (const q of queries[1].values()) {
                // await this.handleDocAdd(q, doc, true, docCache)
                if (this.match(q, doc, q.options?.lookup !== undefined)) {
                    await this.handleDocAdd(q, doc, true, docCache);
                }
                await this.handleDocAddLookup(q, doc);
                await this.handleDocAddRelation(q, doc);
            }
        }
        return {};
    }
    async handleDocAdd(q, doc, handleLookup = true, docCache) {
        let needPush = true;
        if (q.result instanceof Promise) {
            q.result = await q.result;
        }
        if (q.options?.lookup !== undefined && handleLookup) {
            await this.lookup(q._class, doc, q.options.lookup);
            const matched = this.match(q, doc);
            if (!matched)
                needPush = false;
        }
        if (needPush) {
            // We could already have document inside results, if query is created during processing of document create transaction and not yet handled on client.
            const pos = q.result.findDoc(doc._id);
            if (pos !== undefined) {
                // No need to update, document already in results.
                needPush = false;
            }
        }
        if (needPush) {
            // If query contains search we must check use fulltext
            if (q.query.$search != null && q.query.$search.length > 0) {
                const match = await this.client.findOne(q._class, {
                    $search: q.query.$search,
                    $searchStrict: q.query.$searchStrict,
                    _id: doc._id
                }, q.options);
                if (match === undefined)
                    return;
            }
            q.result.push(doc);
            if (q.options?.total === true) {
                q.total++;
            }
            if (q.options?.sort !== undefined) {
                q.result.sort(q._class, q.options.sort, this.getHierarchy(), this.client.getModel());
            }
            if (q.options?.limit !== undefined && q.result.length > q.options.limit) {
                if (q.result.pop()?._id !== doc._id || q.options?.total === true) {
                    await this.callback(q, true);
                }
            }
            else {
                await this.callback(q, true);
            }
        }
    }
    async callback(q, bulkUpdate = false) {
        if (q.result instanceof Promise) {
            q.result = await q.result;
        }
        const result = q.result;
        this.refs.updateDocuments(q, result.getDocs());
        if (bulkUpdate) {
            this.queriesToUpdate.set(q.id, q);
        }
        else {
            this.queriesToUpdate.delete(q.id);
            for (const [id, callback] of q.callbacks.entries()) {
                callback((0, core_1.toFindResult)(result.getResult(id), q.total));
            }
        }
    }
    async handleDocAddRelation(q, doc) {
        if (q.options?.associations === undefined)
            return;
        if (doc._class !== core_1.default.class.Relation)
            return;
        const relation = doc;
        const assoc = findAssociation(q.options.associations, relation.association);
        if (assoc !== undefined) {
            if (q.result instanceof Promise) {
                q.result = await q.result;
            }
            const res = await this.fillRelationDoc(q.result, q.result.getDocs(), q.options.associations, relation);
            if (res) {
                this.queriesToUpdate.set(q.id, q);
            }
        }
    }
    async fillRelationDoc(qRes, docs, associations, relation) {
        if (associations === undefined)
            return false;
        let result = false;
        for (const assoc of associations) {
            if (assoc[0] === relation.association) {
                const association = this.getModel().findObject(assoc[0]);
                if (association === undefined)
                    continue;
                const sourceId = assoc[1] === 1 ? relation.docA : relation.docB;
                const targetId = assoc[1] === 1 ? relation.docB : relation.docA;
                const direct = assoc[1] === 1;
                for (const doc of docs) {
                    if (doc._id === sourceId) {
                        const nestedAssoc = assoc[2];
                        const options = nestedAssoc !== undefined
                            ? {
                                associations: nestedAssoc
                            }
                            : undefined;
                        const docToPush = await this.findOne(direct ? association.classB : association.classA, {
                            _id: targetId
                        }, options);
                        if (docToPush === undefined)
                            continue;
                        doc.$associations ??= {};
                        const key = direct ? 'b' : 'a';
                        const arr = doc.$associations?.[`${assoc[0]}_${key}`] ?? [];
                        const exists = arr.findIndex((p) => p._id === docToPush._id);
                        if (exists !== -1) {
                            arr[exists] = docToPush;
                        }
                        else {
                            arr.push(docToPush);
                        }
                        doc.$associations[`${assoc[0]}_${key}`] = arr;
                        if (qRes.findDoc(doc._id) !== undefined) {
                            qRes.updateDoc(doc, false);
                        }
                        result = true;
                    }
                }
            }
        }
        for (const assoc of associations) {
            if (assoc[2] !== undefined) {
                const key = assoc[1] === 1 ? 'b' : 'a';
                for (const doc of docs) {
                    const arr = doc.$associations?.[`${assoc[0]}_${key}`] ?? [];
                    if (arr.length > 0) {
                        const res = await this.fillRelationDoc(qRes, arr, assoc[2], relation);
                        if (res) {
                            result = true;
                            if (qRes.findDoc(doc._id) !== undefined) {
                                qRes.updateDoc(doc, false);
                            }
                        }
                    }
                }
            }
        }
        return result;
    }
    async handleDocAddLookup(q, doc) {
        if (q.options?.lookup === undefined)
            return;
        const lookup = q.options.lookup;
        if (q.result instanceof Promise) {
            q.result = await q.result;
        }
        let needCallback = false;
        needCallback = this.proccesLookupAddDoc(q.result, lookup, doc);
        if (needCallback) {
            if (q.options?.sort !== undefined) {
                q.result.sort(q._class, q.options.sort, this.getHierarchy(), this.client.getModel());
            }
            await this.callback(q, true);
        }
    }
    proccesLookupAddDoc(docs, lookup, doc) {
        let needCallback = false;
        const lookupWays = this.getLookupWays(lookup, doc._class);
        for (const lookupWay of lookupWays) {
            const [objWay, key, reverseLookupKey] = lookupWay;
            for (const resDoc of docs.getDocs()) {
                const obj = (0, core_1.getObjectValue)(objWay, resDoc);
                if (obj === undefined)
                    continue;
                let value = (0, core_1.getObjectValue)('$lookup.' + key, obj);
                const reverseCheck = reverseLookupKey !== undefined && doc[reverseLookupKey] === obj._id;
                if (value == null && reverseCheck) {
                    value = [];
                    obj.$lookup[key] = value;
                    needCallback = true;
                    docs.updateDoc(resDoc, false);
                }
                if (Array.isArray(value)) {
                    if (this.client.getHierarchy().isDerived(doc._class, core_1.default.class.AttachedDoc) && reverseCheck) {
                        const idx = value.findIndex((p) => p._id === doc._id);
                        if (idx === -1) {
                            value.push(doc);
                        }
                        else {
                            value[idx] = doc;
                        }
                        needCallback = true;
                        docs.updateDoc(resDoc, false);
                    }
                }
                else {
                    if (obj[key] === doc._id) {
                        obj.$lookup[key] = doc;
                        needCallback = true;
                        docs.updateDoc(resDoc, false);
                    }
                }
            }
        }
        return needCallback;
    }
    async txRemoveDoc(tx, docCache) {
        for (const queries of this.queries.entries()) {
            const isTx = this.client.getHierarchy().isDerived(queries[0], core_1.default.class.Tx);
            for (const q of queries[1].values()) {
                if (isTx) {
                    // handle add since Txes are immutable
                    // await this.handleDocAdd(q, tx, true, docCache)
                    if (this.match(q, tx, q.options?.lookup !== undefined)) {
                        await this.handleDocAdd(q, tx, true, docCache);
                    }
                    await this.handleDocAddLookup(q, tx);
                    continue;
                }
                await this.handleDocRemove(q, tx);
            }
        }
        return {};
    }
    async handleDocRemove(q, tx) {
        const h = this.client.getHierarchy();
        if (q._class === tx.objectClass || h.isDerived(q._class, tx.objectClass) || h.isDerived(tx.objectClass, q._class)) {
            if (q.result instanceof Promise) {
                q.result = await q.result;
            }
            const index = q.result.getDocs().find((p) => p._id === tx.objectId);
            if (index !== undefined) {
                if (q.options?.limit !== undefined && q.options.limit === q.result.length && q.query._id !== tx.objectId) {
                    await this.refresh(q);
                    return;
                }
                q.result.delete(index._id);
                this.refs.updateDocuments(q, [index], true);
                if (q.options?.total === true) {
                    q.total--;
                }
                await this.callback(q, true);
            }
        }
        await this.handleDocRemoveLookup(q, tx);
        await this.handleDocRemoveRelation(q, tx);
    }
    async handleDocRemoveRelation(q, tx) {
        if (q.options?.associations === undefined)
            return;
        if (tx.objectClass !== core_1.default.class.Relation)
            return;
        await this.refresh(q);
    }
    async handleDocRemoveLookup(q, tx) {
        if (q.options?.lookup === undefined)
            return;
        let needCallback = false;
        const lookupWays = this.getLookupWays(q.options.lookup, tx.objectClass);
        if (lookupWays.length === 0)
            return;
        if (q.result instanceof Promise) {
            q.result = await q.result;
        }
        for (const lookupWay of lookupWays) {
            const [objWay, key] = lookupWay;
            const docs = q.result;
            for (const doc of docs.getDocs()) {
                const obj = (0, core_1.getObjectValue)(objWay, doc);
                if (obj === undefined)
                    continue;
                const value = (0, core_1.getObjectValue)('$lookup.' + key, obj);
                if (value === undefined)
                    continue;
                if (Array.isArray(value)) {
                    const index = value.findIndex((p) => p._id === tx.objectId);
                    if (index !== -1) {
                        value.splice(index, 1);
                        needCallback = true;
                        docs.updateDoc(doc, false);
                    }
                }
                else {
                    if (value._id === tx.objectId) {
                        obj.$lookup[key] = undefined;
                        needCallback = true;
                        docs.updateDoc(doc, false);
                    }
                }
            }
        }
        if (needCallback) {
            if (q.options?.sort !== undefined) {
                q.result.sort(q._class, q.options.sort, this.getHierarchy(), this.client.getModel());
            }
            await this.callback(q, true);
        }
    }
    getLookupWays(lookup, _class, parent = '') {
        const result = [];
        const hierarchy = this.client.getHierarchy();
        if (lookup._id !== undefined) {
            for (const key of Object.keys(lookup._id)) {
                const value = lookup._id[key];
                const [valueClass, reverseLookupKey] = Array.isArray(value) ? value : [value, 'attachedTo'];
                const clazz = hierarchy.isMixin(valueClass) ? hierarchy.getBaseClass(valueClass) : valueClass;
                if (hierarchy.isDerived(_class, clazz)) {
                    result.push([parent, key, reverseLookupKey]);
                }
            }
        }
        for (const key in lookup) {
            if (key === '_id')
                continue;
            const value = lookup[key];
            if (Array.isArray(value)) {
                const clazz = hierarchy.isMixin(value[0]) ? hierarchy.getBaseClass(value[0]) : value[0];
                if (hierarchy.isDerived(_class, clazz)) {
                    result.push([parent, key]);
                }
                const lookupKey = '$lookup.' + key;
                const newParent = parent.length > 0 ? parent + '.' + lookupKey : lookupKey;
                const nested = this.getLookupWays(value[1], _class, newParent);
                if (nested.length > 0) {
                    result.push(...nested);
                }
            }
            else {
                const clazz = hierarchy.isMixin(value) ? hierarchy.getBaseClass(value) : value;
                if (hierarchy.isDerived(_class, clazz)) {
                    result.push([parent, key]);
                }
            }
        }
        return result;
    }
    async _tx(tx, docCache) {
        switch (tx._class) {
            case core_1.default.class.TxCreateDoc:
                return await this.txCreateDoc(tx, docCache);
            case core_1.default.class.TxUpdateDoc:
                return await this.txUpdateDoc(tx, docCache);
            case core_1.default.class.TxRemoveDoc:
                return await this.txRemoveDoc(tx, docCache);
            case core_1.default.class.TxMixin:
                return await this.txMixin(tx, docCache);
            case core_1.default.class.TxApplyIf:
                return await Promise.resolve([]);
        }
        return {};
    }
    async tx(...txes) {
        const result = [];
        const docCache = new Map();
        for (const tx of txes) {
            if (tx._class === core_1.default.class.TxWorkspaceEvent) {
                const evt = tx;
                await this.checkUpdateEvents(evt);
                await this.changePrivateHandler(evt);
            }
            result.push(await this._tx(tx, docCache));
        }
        if (this.queriesToUpdate.size > 0) {
            const copy = new Map(this.queriesToUpdate);
            this.queriesToUpdate.clear();
            for (const q of copy.values()) {
                if (q.result instanceof Promise) {
                    q.result = await q.result;
                }
                const qr = q.result;
                for (const [id, callback] of q.callbacks.entries()) {
                    callback((0, core_1.toFindResult)(qr.getResult(id), q.total));
                }
            }
        }
        return result;
    }
    async checkUpdateEvents(evt, trigger = true) {
        const h = this.client.getHierarchy();
        function hasClass(q, classes) {
            return classes.includes(q._class) || classes.some((it) => h.isDerived(q._class, it) || h.isDerived(it, q._class));
        }
        if (evt.event === core_1.WorkspaceEvent.IndexingUpdate) {
            const indexingParam = evt.params;
            for (const q of [...this.queue.values()]) {
                if (hasClass(q, indexingParam._class) && q.query.$search !== undefined) {
                    if (!this.removeFromQueue(q)) {
                        try {
                            await this.refresh(q);
                        }
                        catch (err) {
                            analytics_1.Analytics.handleError(err);
                            console.error(err);
                        }
                    }
                    else {
                        this.removeQueue(q);
                    }
                }
            }
            for (const v of this.queries.values()) {
                for (const q of v.values()) {
                    if (hasClass(q, indexingParam._class) && q.query.$search !== undefined) {
                        try {
                            await this.refresh(q);
                        }
                        catch (err) {
                            analytics_1.Analytics.handleError(err);
                            console.error(err);
                        }
                    }
                }
            }
        }
        if (evt.event === core_1.WorkspaceEvent.BulkUpdate) {
            const params = evt.params;
            for (const q of [...this.queue.values()]) {
                if (hasClass(q, params._class)) {
                    if (!this.removeFromQueue(q)) {
                        try {
                            await this.refresh(q);
                        }
                        catch (err) {
                            analytics_1.Analytics.handleError(err);
                            console.error(err);
                        }
                    }
                }
            }
            for (const v of this.queries.values()) {
                for (const q of v.values()) {
                    if (hasClass(q, params._class)) {
                        try {
                            await this.refresh(q);
                        }
                        catch (err) {
                            analytics_1.Analytics.handleError(err);
                            console.error(err);
                        }
                    }
                }
            }
        }
    }
    async changePrivateHandler(evt) {
        if (evt.event === core_1.WorkspaceEvent.SecurityChange) {
            for (const q of [...this.queue.values()]) {
                if (typeof q.query.space !== 'string' || q.query.space === evt.objectSpace) {
                    if (!this.removeFromQueue(q)) {
                        try {
                            await this.refresh(q);
                        }
                        catch (err) {
                            analytics_1.Analytics.handleError(err);
                            console.error(err);
                        }
                    }
                }
            }
            for (const v of this.queries.values()) {
                for (const q of v.values()) {
                    if (typeof q.query.space !== 'string' || q.query.space === evt.objectSpace) {
                        try {
                            await this.refresh(q);
                        }
                        catch (err) {
                            analytics_1.Analytics.handleError(err);
                            console.error(err);
                        }
                    }
                }
            }
        }
    }
    async __updateLookup(q, updatedDoc, ops) {
        for (const key in ops) {
            if (!key.startsWith('$')) {
                if (q.options !== undefined) {
                    const lookup = q.options.lookup?.[key];
                    if (lookup !== undefined) {
                        const lookupClass = getLookupClass(lookup);
                        const nestedLookup = getNestedLookup(lookup);
                        if (Array.isArray(ops[key])) {
                            ;
                            updatedDoc.$lookup[key] = await this.findAll(lookupClass, { _id: { $in: ops[key] } }, { lookup: nestedLookup });
                        }
                        else {
                            ;
                            updatedDoc.$lookup[key] = await this.findOne(lookupClass, { _id: ops[key] }, { lookup: nestedLookup });
                        }
                    }
                }
            }
            else {
                if (key === '$push') {
                    const pops = ops[key] ?? {};
                    for (const pkey of Object.keys(pops)) {
                        if (q.options !== undefined) {
                            const lookup = q.options.lookup?.[pkey];
                            if (lookup !== undefined) {
                                const lookupClass = getLookupClass(lookup);
                                const nestedLookup = getNestedLookup(lookup);
                                const pp = updatedDoc.$lookup;
                                if (pp[pkey] === undefined) {
                                    pp[pkey] = [];
                                }
                                if (Array.isArray(pops[pkey])) {
                                    const pushData = await this.findAll(lookupClass, { _id: { $in: pops[pkey] } }, { lookup: nestedLookup });
                                    pp[pkey].push(...pushData);
                                }
                                else {
                                    const d = await this.findOne(lookupClass, { _id: pops[pkey] }, { lookup: nestedLookup });
                                    if (d !== undefined) {
                                        pp[pkey].push(d);
                                    }
                                }
                            }
                        }
                    }
                }
                else if (key === '$pull') {
                    const pops = ops[key] ?? {};
                    for (const pkey of Object.keys(pops)) {
                        if (q.options !== undefined) {
                            const lookup = q.options.lookup?.[pkey];
                            if (lookup !== undefined) {
                                const pid = pops[pkey];
                                const pp = updatedDoc.$lookup;
                                if (pp[pkey] === undefined) {
                                    pp[pkey] = [];
                                }
                                if (Array.isArray(pid)) {
                                    pp[pkey] = pp[pkey].filter((it) => !pid.includes(it._id));
                                }
                                else {
                                    pp[pkey] = pp[pkey].filter((it) => it._id !== pid);
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    async __updateDoc(q, updatedDoc, tx) {
        if (q.result instanceof results_1.ResultArray) {
            q.result.clearLoadedModifiedOn(tx.objectId);
        }
        core_1.TxProcessor.updateDoc2Doc(updatedDoc, tx);
        const ops = {
            ...tx.operations,
            modifiedBy: tx.modifiedBy,
            modifiedOn: tx.modifiedOn
        };
        await this.__updateLookup(q, updatedDoc, ops);
    }
    async sort(q, tx) {
        const sort = q.options?.sort;
        if (sort === undefined)
            return;
        let needSort = sort.modifiedBy !== undefined || sort.modifiedOn !== undefined;
        if (!needSort)
            needSort = this.checkNeedSort(sort, tx);
        if (needSort) {
            if (q.result instanceof Promise) {
                q.result = await q.result;
            }
            q.result.sort(q._class, sort, this.getHierarchy(), this.client.getModel());
        }
    }
    checkNeedSort(sort, tx) {
        const ops = tx._class === core_1.default.class.TxMixin
            ? tx.attributes
            : tx.operations;
        for (const key in ops) {
            if (key.startsWith('$')) {
                for (const opKey in ops[key]) {
                    if (opKey in sort)
                        return true;
                }
            }
            else {
                if (key in sort)
                    return true;
            }
        }
        return false;
    }
    async updatedDocCallback(q, res, updatedDoc) {
        if (q.options?.limit !== undefined && res.length > q.options.limit) {
            if (updatedDoc === undefined) {
                await this.refresh(q);
                return;
            }
            if (res.getDocs()[q.options?.limit]._id === updatedDoc._id) {
                await this.refresh(q);
                return;
            }
            if (res.pop()?._id !== updatedDoc._id) {
                await this.callback(q, true);
            }
        }
        else {
            await this.callback(q, true);
        }
    }
}
exports.LiveQuery = LiveQuery;
function getNestedLookup(lookup) {
    return Array.isArray(lookup) ? lookup[1] : undefined;
}
function getLookupClass(lookup) {
    return Array.isArray(lookup) ? lookup[0] : lookup;
}
function findAssociation(associations, association) {
    for (const assoc of associations) {
        if (assoc[0] === association)
            return assoc;
    }
    for (const assoc of associations) {
        if (assoc[2] !== undefined) {
            const res = findAssociation(assoc[2], association);
            if (res !== undefined) {
                return assoc;
            }
        }
    }
}
//# sourceMappingURL=index.js.map